import React from 'react';
import type { FeedbackCategory, FeedbackTopic } from '../../types';

interface FeedbackListProps {
  categories: FeedbackCategory[];
  topics: FeedbackTopic[];
  loading: boolean;
  selectedCategory: number | null;
  selectedStatus: string;
  searchQuery: string;
  sortBy: string;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onTopicClick: (topic: FeedbackTopic) => void;
  onSearch: (query: string) => void;
  onCategoryFilter: (categoryId: number | null) => void;
  onStatusFilter: (status: string) => void;
  onSortChange: (sort: string) => void;
  onPageChange: (page: number) => void;
}

const FeedbackList: React.FC<FeedbackListProps> = ({
  categories,
  topics,
  loading,
  selectedCategory,
  selectedStatus,
  searchQuery,
  sortBy,
  currentPage,
  totalPages,
  totalCount,
  onTopicClick,
  onSearch,
  onCategoryFilter,
  onStatusFilter,
  onSortChange,
  onPageChange
}) => {
  const getStatusBadge = (status: string) => {
    const statusMap = {
      'new': { text: '新建', class: 'bg-primary' },
      'processing': { text: '处理中', class: 'bg-warning' },
      'resolved': { text: '已解决', class: 'bg-success' },
      'closed': { text: '已关闭', class: 'bg-secondary' }
    };
    const statusInfo = statusMap[status as keyof typeof statusMap] || { text: status, class: 'bg-secondary' };
    return (
      <span className={`badge ${statusInfo.class}`}>
        {statusInfo.text}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return '今天';
    } else if (diffDays === 1) {
      return '昨天';
    } else if (diffDays < 7) {
      return `${diffDays}天前`;
    } else {
      return date.toLocaleDateString('zh-CN');
    }
  };

  return (
    <div className="feedback-list">
      {/* 筛选和搜索栏 */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                {/* 搜索框 */}
                <div className="col-md-6">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control feedback-search-input"
                      placeholder="搜索反馈..."
                      value={searchQuery}
                      onChange={(e) => onSearch(e.target.value)}
                    />
                    <i className="fas fa-search feedback-search-icon"></i>
                  </div>
                </div>

                {/* 分类筛选 */}
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={selectedCategory || ''}
                    onChange={(e) => onCategoryFilter(e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">所有分类</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 状态筛选 */}
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={selectedStatus}
                    onChange={(e) => onStatusFilter(e.target.value)}
                  >
                    <option value="">所有状态</option>
                    <option value="new">新建</option>
                    <option value="processing">处理中</option>
                    <option value="resolved">已解决</option>
                    <option value="closed">已关闭</option>
                  </select>
                </div>

                {/* 排序 */}
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => onSortChange(e.target.value)}
                  >
                    <option value="latest">最新发布</option>
                    <option value="oldest">最早发布</option>
                    <option value="replies">回复最多</option>
                    <option value="views">浏览最多</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 统计信息 */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted">
              共找到 {totalCount} 条反馈
            </span>
            <span className="text-muted">
              第 {currentPage} 页，共 {totalPages} 页
            </span>
          </div>
        </div>
      </div>

      {/* 反馈列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">加载中...</span>
          </div>
          <p className="mt-2 text-muted">加载中...</p>
        </div>
      ) : topics.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
          <p className="text-muted">暂无反馈数据</p>
        </div>
      ) : (
        <div className="row">
          {topics.map(topic => (
            <div key={topic.id} className="col-12 mb-3">
              <div 
                className={`card h-100 cursor-pointer ${topic.is_pinned ? 'border-warning' : ''}`}
                onClick={() => onTopicClick(topic)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-2">
                        {topic.is_pinned && (
                          <i className="fas fa-thumbtack text-warning me-2" title="置顶"></i>
                        )}
                        <h5 className="card-title mb-0">{topic.title}</h5>
                      </div>
                      
                      <div className="d-flex align-items-center mb-2">
                        <span className={`badge bg-${topic.category.color} me-2`}>
                          <i className={`${topic.category.icon} me-1`}></i>
                          {topic.category.name}
                        </span>
                        {getStatusBadge(topic.status)}
                      </div>
                      
                      <div className="d-flex align-items-center text-muted small">
                        <span className="me-3">
                          <i className="fas fa-user me-1"></i>
                          {topic.user.username}
                        </span>
                        <span className="me-3">
                          <i className="fas fa-clock me-1"></i>
                          {formatDate(topic.created_at)}
                        </span>
                        <span className="me-3">
                          <i className="fas fa-comment me-1"></i>
                          {topic.reply_count} 回复
                        </span>
                        <span>
                          <i className="fas fa-eye me-1"></i>
                          {topic.view_count} 浏览
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="row mt-4">
          <div className="col-12">
            <nav aria-label="反馈列表分页">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    上一页
                  </button>
                </li>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => onPageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    下一页
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackList;
